/**
 * Ticket 9782 — the unsaved-changes guard of the shared drawer shell.
 *
 * Kept apart from Drawer.vue so the wording, the "is this a Cancel button"
 * rule and the list of pickers that render outside the panel can be tested and
 * changed in one place.
 */
import { onBeforeUnmount, onMounted, ref } from 'vue';
import type { InjectionKey } from 'vue';
import Swal from 'sweetalert2';

export interface DrawerGuard {
  /** Call when the form changed in a way the shell cannot see. */
  markDirty: () => void;
  /** Call after a save that leaves the drawer open. */
  markClean: () => void;
  /** Asks "Discard?" when something was entered; true when closing may go on. */
  mayClose: () => Promise<boolean>;
}

export const DRAWER_GUARD: InjectionKey<DrawerGuard> = Symbol('drawerGuard');

/** "Discard unsaved changes?" True when the person chose to discard. */
export const confirmDiscard = async (): Promise<boolean> => {
  const result = await Swal.fire({
    title: 'Discard unsaved changes?',
    text: 'What you entered in this form has not been saved and will be lost.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Discard',
    cancelButtonText: 'Keep editing',
    focusCancel: true,
  });

  return !!result.isConfirmed;
};

const CANCEL_WORDS = /^(cancel|close|discard)$/i;

/**
 * A button that abandons the form: marked with `data-drawer-cancel`, or
 * labelled exactly Cancel, Close or Discard. `data-drawer-cancel="false"`
 * opts a button out.
 */
export const isExplicitCancel = (button: Element): boolean => {
  const mark = button.getAttribute('data-drawer-cancel');
  if (mark === 'false') return false;
  if (mark !== null) return true;

  return CANCEL_WORDS.test((button.textContent ?? '').replace(/\s+/g, ' ').trim());
};

/** Options of a dropdown and the cells of a date picker, both rendered on the page body. */
const OUTSIDE_EDITS =
  '.vs__dropdown-option, .dp__cell_inner, .dp__overlay_cell, .dp__action_select, .dp__time_col button';

/** Edits made with a button inside the panel, which fire no input or change event. */
const BUTTON_EDITS =
  '.vs__clear, .vs__deselect, [aria-pressed], [role="switch"], [role="radio"], [role="checkbox"]';

export const isEditOutsidePanel = (target: EventTarget | null): boolean =>
  !!(target as Element | null)?.closest?.(OUTSIDE_EDITS);

/**
 * The guard itself, for the shared Drawer and for the few drawers that are
 * still hand-built. Bind `onEdit` to the panel's `input` and `change` (on the way up, NOT capture,
 * and never read `dirty` in a template: see Drawer.vue)
 * and `onInnerClick` to its `click` (capture); close through `mayClose`.
 *
 * `dirty` turns true on the first edit inside the panel (typing, a tick, a
 * file, a dropdown or date pick) and stays true until the form says it is
 * clean or the drawer is unmounted, which is what a successful save does.
 */
export function useFormGuard(options: {
  /** Is this drawer a form at all? */
  enabled: () => boolean;
  /** Is it the topmost open drawer? Picks on the page body belong to that one. */
  isTop?: () => boolean;
  /** The drawer's own × button, which closes through `mayClose` directly. */
  ownClose?: () => HTMLElement | null;
}) {
  const dirty = ref(false);
  const asking = ref(false);
  /** Set while a confirmed Cancel click is passed on to the form's own handler. */
  let passing = false;

  const onEdit = (e: Event): void => {
    if (!options.enabled()) return;
    const t = e.target as HTMLElement | null;
    // Typing in a search box or a dropdown's filter changes nothing to lose.
    if (t?.closest?.('[type="search"], .vs__search, [data-no-dirty]')) return;
    dirty.value = true;
  };

  /** May the drawer close now? Asks once when something was entered. */
  const mayClose = async (): Promise<boolean> => {
    if (!options.enabled() || !dirty.value) return true;
    if (asking.value) return false;
    asking.value = true;
    try {
      return await confirmDiscard();
    } finally {
      asking.value = false;
    }
  };

  /**
   * A Cancel / Close button belongs to the form inside, with its own handler,
   * so it is caught on the way down: when something was entered the click is
   * held, the question asked, and on "Discard" the same click is sent on.
   */
  const onInnerClick = (e: MouseEvent): void => {
    if (!options.enabled()) return;
    const t = e.target as HTMLElement | null;
    // Clearing a dropdown, removing one of its values, or flipping a toggle
    // built from a button (a chip, a switch) is an edit too.
    if (t?.closest?.(BUTTON_EDITS)) dirty.value = true;
    if (passing || !dirty.value) return;
    const button = t?.closest?.('button, [role="button"]') as HTMLElement | null;
    if (!button || button === options.ownClose?.() || !isExplicitCancel(button)) return;
    e.preventDefault();
    e.stopPropagation();
    void mayClose().then((ok) => {
      if (!ok) return;
      passing = true;
      try {
        button.click();
      } finally {
        passing = false;
      }
    });
  };

  /** Dropdown menus and date pickers render outside the panel, on the page body. */
  const onPageEdit = (e: Event): void => {
    if (options.enabled() && (options.isTop?.() ?? true) && isEditOutsidePanel(e.target)) {
      dirty.value = true;
    }
  };

  onMounted(() => {
    try {
      document.addEventListener('mousedown', onPageEdit, true);
      document.addEventListener('click', onPageEdit, true);
    } catch {
      /* no document (tests) */
    }
  });
  onBeforeUnmount(() => {
    try {
      document.removeEventListener('mousedown', onPageEdit, true);
      document.removeEventListener('click', onPageEdit, true);
    } catch {
      /* no document (tests) */
    }
  });

  const guard: DrawerGuard = {
    markDirty: () => {
      dirty.value = true;
    },
    markClean: () => {
      dirty.value = false;
    },
    mayClose,
  };

  return { dirty, onEdit, onInnerClick, mayClose, guard };
}
